import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import clsx from 'clsx';
import { Tooltip } from '@hevoio/hevo-ui-toolkit';
import { useKeyboardShortcutListeners } from '../../../a11y/shortcuts/hooks/useKeyboardShortcutListeners';
import { SUPPORT_PERSON_DEFAULT_IMAGE_URL } from '../../../support/constants/support';
import { AppbarSupportBtnWrapper } from './appbar-support-cta/AppbarSupportBtnWrapper';
import styles from './Appbar.module.scss';
import { AppbarItem } from './appbar-item/AppbarItem';
import { SupportService } from '../../../support/interfaces/support';
import { KEYMAP } from '../../../a11y/shortcuts/constants/keyMap';
import { getDocLink, trackDocLink } from '../../../docs/utils/docLinkHandler';
import {
  RELEASE_NOTES_BASE_URL,
  TRACKER_DOCS_CTA_CLICK,
  TRACKER_VERSION_TAG_CLICK,
  TRACKER_VERSION_TAG_MOUSE_ENTER
} from '../../../core/constants/constants';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { ActivationStatusService } from '../../../core/services/activation-status.service';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import useService from '../../../injector/hooks/useService';
import { TeamService } from '../../../core/services/team.service';
import useTeamSettings from '../../../containers/team/hooks/useTeamSettingsService';
import { MultiRegionService } from '../../../auth/services/multi-region.service';
import { HEVO_DARK_THEME_LOGO_SVG } from '../../../shared/constants/brand-logo';
import { WorkspaceSwitcher } from './workspace-switcher/WorkspaceSwitcher';

declare const DASHBOARD_VERSION: any;

interface VersionInfo {
  version: string;
  versionDisplay: string;
  releaseNotesLink: string;
}

interface AppbarProps {
  expanded?: boolean;
}

export function Appbar({ expanded = false }: AppbarProps) {
  const globalShortcutKeys = KEYMAP.keyMaps.global.map;

  const [supportChatHidden, setSupportChatHidden] = useState(false);
  const [isActivateAllowed, setIsActivateAllowed] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionInfo>({
    version: '',
    versionDisplay: '',
    releaseNotesLink: ''
  });
  const [activatedDrawer, setActivatedDrawer] = useState<string>();
  const [supportPersonImageURL, setSupportPersonImageURL] = useState(
    SUPPORT_PERSON_DEFAULT_IMAGE_URL
  );

  const analyticsTracker = useAnalyticsTracker();
  const { isWorkFlowsEnabled, getTeamSettings } = useTeamSettings();
  const history = useHistory();
  const location = useLocation();

  const supportService = useService(SupportService);
  const activateStatusService = useService(ActivationStatusService);
  const { teamDetails } = useService(TeamService);
  const multiRegionService = useService(MultiRegionService);
  const isDpRegion = multiRegionService.getSelectedRegionValue()?.type?.toLocaleLowerCase() === 'dp';

  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    setSupportChatHidden(supportService.isChatHiddenForTeam());
    setIsActivateAllowed(activateStatusService.shouldShowActivation());

    supportService.hideDefaultLauncher();

    supportService.onCallSupportPersonImage$.subscribe(imageURL => {
      setSupportPersonImageURL(imageURL);
    });

    getVersionInfo();
  }, []);

  useEffect(() => {
    const activeDrawer = searchParams.get('drawer');
    setActivatedDrawer(activeDrawer || null);
  }, [location]);

  const getVersionInfo = () => {
    const version = DASHBOARD_VERSION || '';
    let versionDisplay = version;
    let releaseNotesLink: string;

    let releaseVersion = version.match(/v[0-9]+\.[0-9]+/);

    if (releaseVersion) {
      releaseVersion = releaseVersion.toString().replace(/v([0-9]+)\.([0-9])$/, 'v$1.0$2');
    }

    if (releaseVersion) {
      versionDisplay = releaseVersion;
      releaseNotesLink = `${RELEASE_NOTES_BASE_URL + releaseVersion}/`;
    }

    setVersionInfo({
      version,
      versionDisplay,
      releaseNotesLink
    });
  };

  useKeyboardShortcutListeners(
    {
      'global.team': (event: KeyboardEvent) => {
        history.push('/team');
        event.preventDefault();
      },
      'global.payment': (event: KeyboardEvent) => {
        history.push('/payment');
        event.preventDefault();
      }
    },
    {
      priority: 0,
      terminal: 'match'
    }
  );

  const isLinkActive = (path: string) => !!matchPath(path, location.pathname);

  const onVersionTagMouseEnter = () => {
    analyticsTracker.eventTrack({ action: TRACKER_VERSION_TAG_MOUSE_ENTER });
  };

  const onDocsLinkClick = () => {
    trackDocLink('/', 'appbar');
    analyticsTracker.eventTrack({ action: TRACKER_DOCS_CTA_CLICK });
  };

  const onVersionTagClick = () => {
    analyticsTracker.eventTrack({ action: TRACKER_VERSION_TAG_CLICK });
  };

  const isActivateUser = () => teamDetails?.sign_up_source === 'ACTIVATE';

  const dataIdGenerator = getDataIdGenerator('appbar');

  const isJobsViewEnabled = !!getTeamSettings()?.show_jobs_view;

  return (
    <div className={clsx(styles.appbar, expanded && styles.appbarExpanded)}>
      <div className={clsx(styles.appbarHeader, expanded && styles.appbarHeaderExpanded)}>
        <div
          className={clsx(
            styles.appbarBrandImageWrapper,
            expanded && styles.appbarBrandImageExpanded
          )}
        >
          <img
            className={styles.appbarBrandImage}
            src={HEVO_DARK_THEME_LOGO_SVG}
            alt='appbar-brand-img'
          />
        </div>
      </div>

      <div className={styles.appbarContent}>
        <WorkspaceSwitcher expanded={expanded} />
        <div className={clsx(styles.appbarDivider, expanded && styles.appbarDividerExpanded)} />

        <div className={styles.appbarLinks}>
          {isJobsViewEnabled ? (
            <AppbarItem
              navActive={isLinkActive('/all-jobs')}
              to='/all-jobs'
              activeDrawerLink={activatedDrawer}
              iconName='jobs'
              label='All Jobs'
              expanded={expanded}
              onClick={setActivatedDrawer}
            />
          ) : null}

          <AppbarItem
            navActive={isLinkActive('/pipeline/*')}
            to='/pipeline'
            activeDrawerLink={activatedDrawer}
            shortcut={globalShortcutKeys.pipelineList}
            iconName='pipeline'
            label='Pipelines'
            expanded={expanded}
            onClick={setActivatedDrawer}
          />

          {!isDpRegion ? (
            <AppbarItem
              navActive={isLinkActive('/model/*') || isLinkActive('/transform/*')}
              to='/model'
              activeDrawerLink={activatedDrawer}
              shortcut={globalShortcutKeys.modelList}
              iconName='models'
              label='Models'
              expanded={expanded}
              onClick={setActivatedDrawer}
            />
          ) : null}

          {isActivateAllowed && !isDpRegion ? (
            <AppbarItem
              styles={{ order: isActivateUser() ? -1 : 2 }}
              navActive={isLinkActive('/activation/*') || isLinkActive('/target/*')}
              to='/activation'
              activeDrawerLink={activatedDrawer}
              shortcut={globalShortcutKeys.activateList}
              iconName='activate'
              label='Activate'
              expanded={expanded}
              onClick={setActivatedDrawer}
            />
          ) : null}

          {isWorkFlowsEnabled && !isDpRegion ? (
            <AppbarItem
              navActive={isLinkActive('/workflow/*')}
              to='/workflow'
              activeDrawerLink={activatedDrawer}
              shortcut={globalShortcutKeys.workflowList}
              iconName='workflow'
              label='Workflows'
              expanded={expanded}
              onClick={setActivatedDrawer}
            />
          ) : null}

          <AppbarItem
            navActive={isLinkActive('/destination/*')}
            to='/destination'
            activeDrawerLink={activatedDrawer}
            shortcut={globalShortcutKeys.warehouseList}
            iconName='destinations'
            label='Destinations'
            expanded={expanded}
            onClick={setActivatedDrawer}
          />
        </div>

        <div className={`${styles.appbarLinks} ${styles.appbarLinksBottom}`}>
          <AppbarItem
            to={getDocLink('/', 'appbar')}
            target='_blank'
            isExternalLink
            iconName='documentation'
            activeDrawerLink={activatedDrawer}
            label='Docs'
            expanded={expanded}
            onClick={onDocsLinkClick}
          />

          {!supportChatHidden ? (
            <div className={styles.supportBtnWrapper}>
              <AppbarSupportBtnWrapper expanded={expanded} />
            </div>
          ) : null}

          <div className={styles.appbarVersionTagWrapper}>
            <Tooltip dataId='version' title={versionInfo.version} placement='right'>
              <a
                data-id={dataIdGenerator('versionTag')}
                className={clsx(styles.appbarVersionTag)}
                onMouseEnter={onVersionTagMouseEnter}
                onClick={onVersionTagClick}
                target='_blank'
                href={getDocLink(versionInfo.releaseNotesLink || './', 'appbar')}
                rel='noreferrer'
              >
                {versionInfo.versionDisplay}
              </a>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
